import React, { useState, useEffect } from 'react';
import { Send, Users, User, CheckCircle, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { getBackendUrl } from '../utils/getBackendUrl';
import { toast } from 'sonner';

const API = getBackendUrl() + '/api';

export const AdminNotificationPanel = ({ user }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [notificationType, setNotificationType] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.filter(u => u.role !== 'admin'));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!title.trim() || !body.trim()) {
      toast.error('Please enter both title and message');
      return;
    }

    setIsSending(true);

    try {
      const payload = {
        title,
        body,
        icon: '/icon-192.png',
        badge: '/badge-72.png',
        notification_type: 'admin_announcement',
        data: {
          url: '/',
          timestamp: new Date().toISOString()
        }
      };

      if (notificationType === 'all') {
        payload.user_ids = null;
      } else {
        payload.user_ids = selectedUsers;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/push/notify/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send notification');
      }

      const result = await response.json();
      
      if (result.subscriptions_sent === 0) {
        toast.warning(`No notifications sent. Selected users haven't enabled push notifications yet.`);
      } else {
        toast.success(`Notification sent to ${result.subscriptions_sent} device(s)`);
      }

      // Clear form
      setTitle('');
      setBody('');
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error sending notification:', error);
      
      // Show more helpful error messages
      if (error.message.includes('No active subscriptions')) {
        toast.error('No users have enabled push notifications yet. Ask users to enable notifications in Settings first.');
      } else if (error.message.includes('404')) {
        toast.error('No active push subscriptions found');
      } else {
        toast.error(error.message || 'Failed to send notification');
      }
    } finally {
      setIsSending(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <Send className="w-6 h-6 text-blue-400" />
        <h3 className="text-xl font-bold text-white">Send Push Notification</h3>
      </div>

      <form onSubmit={handleSendNotification} className="space-y-4">
        <div>
          <Label htmlFor="notification-title" className="text-slate-300">
            Notification Title
          </Label>
          <Input
            id="notification-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., New Project Update"
            className="mt-1"
            required
          />
        </div>

        <div>
          <Label htmlFor="notification-body" className="text-slate-300">
            Message
          </Label>
          <Textarea
            id="notification-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Enter your notification message..."
            className="mt-1 min-h-[100px]"
            required
          />
        </div>

        <div>
          <Label className="text-slate-300 mb-2 block">Send To</Label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-800">
              <input
                type="radio"
                name="notification-type"
                value="all"
                checked={notificationType === 'all'}
                onChange={(e) => setNotificationType(e.target.value)}
                className="w-4 h-4"
              />
              <Users className="w-5 h-5 text-blue-400" />
              <span className="text-white">All Users</span>
            </label>

            <label className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-800">
              <input
                type="radio"
                name="notification-type"
                value="specific"
                checked={notificationType === 'specific'}
                onChange={(e) => setNotificationType(e.target.value)}
                className="w-4 h-4"
              />
              <User className="w-5 h-5 text-green-400" />
              <span className="text-white">Specific Users</span>
            </label>
          </div>
        </div>

        {notificationType === 'specific' && (
          <div className="max-h-60 overflow-y-auto space-y-2 border border-slate-700 rounded-lg p-3">
            {isLoading ? (
              <p className="text-slate-400 text-sm text-center py-4">Loading users...</p>
            ) : users.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-4">No users available</p>
            ) : (
              users.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 p-2 hover:bg-slate-800/50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.id)}
                    onChange={() => toggleUserSelection(u.id)}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <p className="text-white text-sm">{u.name}</p>
                    <p className="text-slate-400 text-xs">{u.email}</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      u.role === 'employee'
                        ? 'bg-blue-500/20 text-blue-400'
                        : u.role === 'client'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-purple-500/20 text-purple-400'
                    }`}
                  >
                    {u.role}
                  </span>
                </label>
              ))
            )}
          </div>
        )}

        <div className="flex items-center gap-2 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
          <p className="text-sm text-blue-200">
            {notificationType === 'all'
              ? 'This notification will be sent to all users who have enabled push notifications.'
              : `Selected ${selectedUsers.length} user(s) to notify.`}
          </p>
        </div>

        <Button
          type="submit"
          disabled={isSending || (notificationType === 'specific' && selectedUsers.length === 0)}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600"
        >
          {isSending ? 'Sending...' : 'Send Notification'}
        </Button>
      </form>
    </div>
  );
};
